import { useHeaderFilterMenu } from "@/shared/hooks/useHeaderFilterMenu";
import { Portfolio } from "@/types/Portfolio";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { Headline } from "../models/Headline";
import { mapDataWithMatchingPortfolios } from "@/utils/portfolioUtils";
import { headlinePortfolioMatcher } from "../utils";
import { useMemo, useState, createElement, useEffect } from "react";
import { useHeadlineQueriesCRUD } from "../api/headline-query/useHeadlineQueriesCRUD";
import { assetClasses } from "../models/categories";
import { FilterMode } from "@/shared/models/filters";
import { FilterMenuConfig } from "@/layout-components/HeaderFilterMenus";
import {
  FilterModeSelector,
  ClearSelectionsFooter,
} from "@/shared/components/FilterComponents";
import { isPrebuiltWatchlist } from "@/pages/PortfolioBuilder/prebuiltWatchlists";

interface HeadlineFilterOption {
  setActions?: (actions: React.ReactElement[]) => void;
  pushAction?: (action: React.ReactElement) => void;
  persistKey?: string;
}

export const useHeadlinesHighlightFilterMenu = (
  options: HeadlineFilterOption
) => {
  const { portfolios } = usePortfoliosCRUD();
  const { queriesQuery: categoryQueries } = useHeadlineQueriesCRUD();
  const [filterMode, setFilterMode] = useState<FilterMode>("AND");
  const [internalFilterSelections, setInternalFilterSelections] = useState<
    Record<string, string[]>
  >({});

  const hasTickersAndTagsSelected = useMemo(
    () =>
      internalFilterSelections["basic-filter"]?.length &&
      (internalFilterSelections["portfolios-filter"]?.length ||
        internalFilterSelections["portfolios-filter-prebuilt"]?.length),
    [internalFilterSelections]
  );

  // Define static filterConfig without disabled states
  // Disabled states will be calculated dynamically in HeaderFilterMenus based on selections
  const filterConfig = useMemo(() => {
    // Create filter mode selector for the Filter section only
    const filterModeFooter = createElement(FilterModeSelector, {
      filterMode,
      onFilterModeChange: setFilterMode,
      disabled: !hasTickersAndTagsSelected,
    });

    // Separate user and prebuilt watchlists using owner_type
    const userWatchlists = portfolios.filter((p) => !isPrebuiltWatchlist(p));
    const prebuiltWatchlists = portfolios.filter((p) => isPrebuiltWatchlist(p));

    // User watchlist options
    const userWatchlistOptions = userWatchlists.map((portfolio) => ({
      id: String(portfolio.id),
      label: portfolio.name,
    }));

    // Prebuilt watchlist options
    const prebuiltWatchlistOptions = prebuiltWatchlists.map((watchlist) => ({
      id: String(watchlist.id),
      label: watchlist.name,
    }));

    return [
      // Filter sections - Your Watchlists
      {
        id: "portfolios-filter",
        label: "Filter by Your Watchlists",
        description: "Mutually exclusive with Advanced Filters",
        filterOptions: userWatchlistOptions,
        columnIndex: 0,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
      },
      // Filter sections - Newsquawk Watchlists
      {
        id: "portfolios-filter-prebuilt",
        label: "Filter by Newsquawk Watchlists",
        description: "Mutually exclusive with Advanced Filters",
        filterOptions: prebuiltWatchlistOptions,
        columnIndex: 0,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
      },
      {
        id: "basic-filter",
        label: "Filter by Asset Class",
        filterOptions: assetClasses.map((asset) => ({
          id: String(asset.id),
          label: asset.title,
        })),
        columnIndex: 0,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
        footer: hasTickersAndTagsSelected ? filterModeFooter : null,
      },
      // Highlight sections - Your Watchlists
      {
        id: "portfolios-highlight",
        label: "Highlight by Your Watchlists",
        filterOptions: userWatchlistOptions,
        columnIndex: 1,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
      },
      // Highlight sections - Newsquawk Watchlists
      {
        id: "portfolios-highlight-prebuilt",
        label: "Highlight by Newsquawk Watchlists",
        filterOptions: prebuiltWatchlistOptions,
        columnIndex: 1,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
      },
      {
        id: "category-filter",
        label: "Advanced Filters",
        description: "Mutually exclusive with basic filters",
        filterOptions:
          categoryQueries.data?.map((query) => ({
            id: String(query.id),
            label: query.queryName,
          })) || [],
        columnIndex: 1,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
      },
    ] as FilterMenuConfig[];
  }, [filterMode, hasTickersAndTagsSelected, portfolios, categoryQueries.data]);

  // Create global footer component with just clear selections button
  const globalFooter = useMemo(
    () => createElement(ClearSelectionsFooter, {}),
    []
  );

  const [filterSelections, setFilterSelections] = useHeaderFilterMenu(
    filterConfig,
    options.pushAction,
    options.persistKey,
    undefined,
    globalFooter
  );

  useEffect(() => {
    setInternalFilterSelections(filterSelections);
  }, [filterSelections]);

  return [filterSelections, setFilterSelections, filterMode] as const;
};

export const filterByWatchlists = (
  headlines: Headline[],
  selectedPortfolios: Portfolio[]
) => {
  return headlines.filter((headline) =>
    headlinePortfolioMatcher(headline, selectedPortfolios)
  );
};

export const applyFilters = (
  headlines: Headline[],
  filterSelections: Record<string, string[]>,
  portfolios: Portfolio[] // portfolios from API now include both user and prebuilt
) => {
  // Combine highlight selections from both ticker and keyword watchlist sections
  const highlightSelections = [
    ...(filterSelections["ticker-watchlists-highlight"] || []),
    ...(filterSelections["keyword-watchlists-highlight"] || []),
  ];

  const portfoliosToHighlight = portfolios.filter((portfolio) =>
    highlightSelections.includes(String(portfolio.id))
  );

  return mapDataWithMatchingPortfolios(
    headlines,
    [],
    portfoliosToHighlight,
    headlinePortfolioMatcher
  );
};
